import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';

import {
  getRegimes,
  createRegime,
  getRegimeGroups,
  createRegimeGroup,
  updateRegimeGroup,
  deleteRegimeGroup,
  updateRegime,
  deleteRegime,
  copyRegime,
  getAcademicRanks,
} from 'api/aid-models/aid-regimes.api';
import { getCustomLogic } from 'api/aid-models/aid-custom-logic.api';

type ModelId = string | number;
type RegimeId = string | number;
type GroupId = string | number;
type ClientId = string | number;

interface UseModelScopedParams {
  userId: string;
  modelId: ModelId;
  enabled?: boolean;
}

interface CreateRegimeVars {
  userId: string;
  modelId: ModelId;
  data: unknown;
}

interface UpdateGroupVars {
  userId: string;
  modelId: ModelId;
  groupId: GroupId;
  data: unknown;
}

interface DeleteGroupVars {
  userId: string;
  modelId: ModelId;
  groupId: GroupId;
}

interface UpdateRegimeVars {
  userId: string;
  modelId: ModelId;
  regimeId: RegimeId;
  data: unknown;
}

interface DeleteRegimeVars {
  userId: string;
  modelId: ModelId;
  regimeId: RegimeId;
}

interface CopyRegimeVars {
  userId: string;
  modelId: ModelId;
  regimeId: RegimeId;
  data: unknown;
}

interface UseAcademicRanksParams {
  userId: string;
  clientId: ClientId;
  enrollmentType: string;
  enabled?: boolean;
}

interface UseCustomLogicParams {
  userId: string;
  clientId: ClientId;
  enabled?: boolean;
}

export const regimeKeys = {
  all: ['regimes'] as const,
  model: (modelId: ModelId): readonly unknown[] => [
    ...regimeKeys.all,
    'model',
    modelId,
  ],
};

export const regimeGroupKeys = {
  all: ['regimeGroups'] as const,
  model: (modelId: ModelId): readonly unknown[] => [
    ...regimeGroupKeys.all,
    'model',
    modelId,
  ],
};

const invalidateRegimeQueries = (queryClient: QueryClient): void => {
  queryClient.invalidateQueries({ queryKey: regimeKeys.all });
  queryClient.invalidateQueries({ queryKey: regimeGroupKeys.all });
};

export const useRegimes = ({
  userId,
  modelId,
  enabled = true,
}: UseModelScopedParams) =>
  useQuery({
    queryKey: regimeKeys.model(modelId),
    queryFn: () => getRegimes(userId, modelId),
    enabled: enabled && !!userId && !!modelId,
  });

export const useRegimeGroups = ({
  userId,
  modelId,
  enabled = true,
}: UseModelScopedParams) =>
  useQuery({
    queryKey: regimeGroupKeys.model(modelId),
    queryFn: () => getRegimeGroups(userId, modelId),
    enabled: enabled && !!userId && !!modelId,
  });

export const useCreateRegime = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, modelId, data }: CreateRegimeVars) =>
      createRegime(userId, modelId, data),
    onSuccess: () => invalidateRegimeQueries(queryClient),
  });
};

export const useCreateRegimeGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, modelId, data }: CreateRegimeVars) =>
      createRegimeGroup(userId, modelId, data),
    onSuccess: () => invalidateRegimeQueries(queryClient),
  });
};

export const useUpdateRegimeGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, modelId, groupId, data }: UpdateGroupVars) =>
      updateRegimeGroup(userId, modelId, groupId, data),
    onSuccess: () => invalidateRegimeQueries(queryClient),
  });
};

export const useDeleteRegimeGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, modelId, groupId }: DeleteGroupVars) =>
      deleteRegimeGroup(userId, modelId, groupId),
    onSuccess: () => invalidateRegimeQueries(queryClient),
  });
};

export const useUpdateRegime = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, modelId, regimeId, data }: UpdateRegimeVars) =>
      updateRegime(userId, modelId, regimeId, data),
    onSuccess: () => invalidateRegimeQueries(queryClient),
  });
};

export const useDeleteRegime = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, modelId, regimeId }: DeleteRegimeVars) =>
      deleteRegime(userId, modelId, regimeId),
    onSuccess: () => invalidateRegimeQueries(queryClient),
  });
};

export const useCopyRegime = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, modelId, regimeId, data }: CopyRegimeVars) =>
      copyRegime(userId, modelId, regimeId, data),
    onSuccess: () => invalidateRegimeQueries(queryClient),
  });
};

export const academicRankKeys = {
  all: ['academicRanks'] as const,
  client: (
    clientId: ClientId,
    enrollmentType: string
  ): readonly unknown[] => [
    ...academicRankKeys.all,
    'client',
    clientId,
    enrollmentType,
  ],
};

export const useAcademicRanks = ({
  userId,
  clientId,
  enrollmentType,
  enabled = true,
}: UseAcademicRanksParams) =>
  useQuery({
    queryKey: academicRankKeys.client(clientId, enrollmentType),
    queryFn: () => getAcademicRanks(userId, clientId, enrollmentType),
    enabled: enabled && !!userId && !!clientId && !!enrollmentType,
    staleTime: 1000 * 60 * 10,
  });

export const customLogicKeys = {
  all: ['customLogic'] as const,
  client: (clientId: ClientId): readonly unknown[] => [
    ...customLogicKeys.all,
    'client',
    clientId,
  ],
};

export const useCustomLogic = ({
  userId,
  clientId,
  enabled = true,
}: UseCustomLogicParams) =>
  useQuery({
    queryKey: customLogicKeys.client(clientId),
    queryFn: () => getCustomLogic(userId, clientId),
    enabled: enabled && !!userId && !!clientId,
    staleTime: 1000 * 60 * 30,
  });
